import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  UserAddForm,
  UserAddInitialValues,
  UserComponents,
  UserAdditionalParams,
  UserFormOptions,
  onUserFormSaved,
  IUserFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateUser, PermissionEnum, } from 'atlas-shared';
import { createUser, fetchUser, getUserCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useQueues, useAgentprofiles, useStatsReports, usePauses, useOrganizations, useAdminprofiles, useSuperadminProfiles, useSkills, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateUser> {}

const UserFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const queues = useQueues();
  const agentprofiles = useAgentprofiles();
  const stats_reports = useStatsReports();
  const pauses = usePauses();
  const organizations = useOrganizations();
  const adminprofiles = useAdminprofiles();
  const superadmin_profiles = useSuperadminProfiles();
  const skills = useSkills();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateUser>>();

  const param_props: IUserFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    queues: queues.queues,
    agentprofiles: agentprofiles.agentprofiles,
    stats_reports: stats_reports.stats_reports,
    pauses: pauses.pauses,
    organizations: organizations.organizations,
    adminprofiles: adminprofiles.adminprofiles,
    superadmin_profiles: superadmin_profiles.superadmin_profiles,
    skills: skills.skills,
  };
  const formLayout = useMemo(() => UserAddForm(values, param_props, t), [values, queues, agentprofiles, stats_reports, pauses, organizations, adminprofiles, superadmin_profiles, skills, t, abilities, ]);
  const options: IFormOptions = useMemo(() => UserFormOptions(values || UserAddInitialValues, param_props, t), [values, queues, agentprofiles, stats_reports, pauses, organizations, adminprofiles, superadmin_profiles, skills, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateUser, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateUser) => save(() => createUser(values).then(
    res => onSaved ? onSaved(res, navigate) : onUserFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchUser(+id)
        .then(({
          login_strategy,
          active,
          two_factor_auth,
          first_name,
          last_name,
          mail_capacity,
          form_capacity,
          chat_capacity,
          settings,
          voice_license,
          voice_settings,
          login_pause,
          dashboard_license,
          reporting_license,
          __organizations,
          notification_settings,
          skills,
        }) => {
          setCloned({
            login_strategy,
            active,
            two_factor_auth,
            first_name,
            last_name,
            mail_capacity,
            form_capacity,
            chat_capacity,
            settings,
            voice_license,
            voice_settings,
            login_pause,
            dashboard_license,
            reporting_license,
            __organizations,
            notification_settings,
            skills,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!queues.loaded || !agentprofiles.loaded || !stats_reports.loaded || !pauses.loaded || !organizations.loaded || !adminprofiles.loaded || !superadmin_profiles.loaded || !skills.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(null, CrudEnum.Insert, PermissionEnum.User, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={UserComponents}
        additionalParams={UserAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...UserAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('USER')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const UserFormAddView = React.memo(withAdd(UserFormAdd, getUserCreateSchema, UserAddInitialValues));
